import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageWrapper } from '../../components/layout';
import { SettingsTab, EmailTestPanel } from '../../components/settings';
import { LedgerDialog, LedgerField } from '../../components/ui';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useToast } from '../../hooks/useToast';
import { SettingCategory, BulkSettingUpdate } from '../../types/settings.types';
import settingsService from '../../services/settings.service';
import { isSameSettingValue } from '../../lib/settingValue';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/cn';

const categoryLabels = {
  [SettingCategory.SYSTEM]: 'System',
  [SettingCategory.EMAIL]: 'Email & SMTP',
  [SettingCategory.SECURITY]: 'Security',
  [SettingCategory.BUSINESS]: 'Business Logic',
  [SettingCategory.UI]: 'User Interface',
};

const tabClass = (on: boolean) =>
  cn(
    'mr-4 cursor-pointer pb-0.5 text-scale-1',
    on ? 'border-b-2 border-accent font-semibold text-accent' : 'text-ink-muted hover:text-ink',
  );

const SettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [values, setValues] = useState<Record<string, any>>({});
  const [changedValues, setChangedValues] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const [resetDialog, setResetDialog] = useState(false);
  const [changeReasonDialog, setChangeReasonDialog] = useState(false);
  const [changeReason, setChangeReason] = useState('');

  const { hasPermission, isSuperuser } = usePermissions();
  const canEditSettings = hasPermission('settings', 'change');

  const queryClient = useQueryClient();
  const { data: settingsByCategory = [], isPending } = useQuery({
    queryKey: ['settings-by-category'],
    queryFn: () => settingsService.getSettingsByCategory(),
  });

  useEffect(() => {
    if (!settingsByCategory.length) return;
    // A background refetch (window refocus, another admin's save) must not
    // clobber in-progress edits; reseed only when nothing is staged.
    if (Object.keys(changedValues).length > 0) return;
    const initial: Record<string, any> = {};
    settingsByCategory.forEach((category) =>
      category.settings.forEach((setting) => {
        initial[setting.key] = setting.typed_value ?? setting.value;
      }),
    );
    setValues(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsByCategory]);

  const safeIndex = settingsByCategory.length ? Math.min(activeTab, settingsByCategory.length - 1) : 0;
  const currentCategory = settingsByCategory[safeIndex];

  const totalSettings = settingsByCategory.reduce((sum, c) => sum + c.settings.length, 0);
  const totalGroups = new Set(settingsByCategory.flatMap((c) => c.settings.map((s) => s.ui_group || 'Other'))).size;
  const changedCount = Object.keys(changedValues).length;

  // The test-mode note must reflect the saved setting, not an unsaved edit
  // in `values` — otherwise the warning could vanish (or wrongly appear)
  // while the user is mid-edit and hasn't saved yet.
  const savedTestMode = settingsByCategory
    .flatMap((c) => c.settings)
    .find((s) => s.key === 'EMAIL_TEST_MODE')?.typed_value;
  const emailTestModePending = 'EMAIL_TEST_MODE' in changedValues;

  const handleChange = (key: string, value: any) => {
    // Handle mutual exclusivity for SSL/TLS settings
    const updatedValues = { ...values };
    const updatedChanges = { ...changedValues };

    if (key === 'EMAIL_USE_TLS' && value === 'true') {
      // If enabling TLS, disable SSL
      updatedValues['EMAIL_USE_SSL'] = 'false';
      const sslInitialValue = settingsByCategory
        .flatMap((cat) => cat.settings)
        .find((s) => s.key === 'EMAIL_USE_SSL')?.typed_value;

      if ('false' !== sslInitialValue) {
        updatedChanges['EMAIL_USE_SSL'] = 'false';
      }
    } else if (key === 'EMAIL_USE_SSL' && value === 'true') {
      // If enabling SSL, disable TLS
      updatedValues['EMAIL_USE_TLS'] = 'false';
      const tlsInitialValue = settingsByCategory
        .flatMap((cat) => cat.settings)
        .find((s) => s.key === 'EMAIL_USE_TLS')?.typed_value;

      if ('false' !== tlsInitialValue) {
        updatedChanges['EMAIL_USE_TLS'] = 'false';
      }
    }

    // Validate the value
    const setting = settingsByCategory
      .flatMap((cat) => cat.settings)
      .find((s) => s.key === key);

    if (setting) {
      const error = settingsService.validateSettingValue(setting, value);
      if (error) {
        setErrors((prev) => ({ ...prev, [key]: error }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[key];
          return newErrors;
        });
      }
    }

    updatedValues[key] = value;
    setValues(updatedValues);

    // Track changed values. Compared through the setting's own type, because
    // the controls hand back strings and typed_value does not: a raw !==
    // between 'true' and true never unstages a toggle put back where it was.
    const initialValue = settingsByCategory
      .flatMap((cat) => cat.settings)
      .find((s) => s.key === key)?.typed_value;

    if (setting && isSameSettingValue(setting, value, initialValue)) {
      delete updatedChanges[key];
    } else {
      updatedChanges[key] = value;
    }

    setChangedValues(updatedChanges);
  };

  const handleSave = () => {
    if (Object.keys(errors).length > 0) {
      toast.error('Please fix validation errors before saving');
      return;
    }

    if (Object.keys(changedValues).length === 0) {
      toast.info('No changes to save');
      return;
    }

    setChangeReasonDialog(true);
  };

  const confirmSave = async () => {
    try {
      setSaving(true);
      setChangeReasonDialog(false);

      const updates: BulkSettingUpdate = {
        settings: Object.entries(changedValues).map(([key, value]) => ({
          key,
          value,
        })),
        change_reason: changeReason,
      };

      const result = await settingsService.bulkUpdateSettings(updates);

      toast.success(result.message);

      setChangedValues({});
      setChangeReason('');

      // Reload settings to get updated values
      await queryClient.invalidateQueries({ queryKey: ['settings-by-category'] });
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      setResetDialog(false);
      setSaving(true);

      const keys = Object.keys(changedValues);
      if (keys.length === 0) {
        // Reset all settings in current category
        if (currentCategory) {
          keys.push(...currentCategory.settings.map((s) => s.key));
        }
      }

      const result = await settingsService.resetToDefaults(keys);

      toast.success(result.message);

      setChangedValues({});
      await queryClient.invalidateQueries({ queryKey: ['settings-by-category'] });
    } catch (error) {
      console.error('Failed to reset settings:', error);
      toast.error('Failed to reset settings');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      const blob = await settingsService.exportSettings();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `settings_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Settings exported successfully');
    } catch (error) {
      console.error('Failed to export settings:', error);
      toast.error('Failed to export settings');
    }
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);

      const result = await settingsService.importSettings({
        settings: data.settings || data,
        override_existing: true,
        change_reason: 'Imported from file',
      });

      toast.success(result.message);

      await queryClient.invalidateQueries({ queryKey: ['settings-by-category'] });
    } catch (error) {
      console.error('Failed to import settings:', error);
      toast.error('Failed to import settings');
    }
  };

  return (
    <PageWrapper
      title="Settings"
      subtitle={isPending ? undefined : `${totalSettings} settings · ${totalGroups} groups`}
      noPaper
      actions={
        canEditSettings ? (
          <span className="flex items-baseline gap-3">
            <button type="button" onClick={handleExport} className="cap-label cursor-pointer hover:!text-ink">
              Export
            </button>
            <button
              type="button"
              onClick={() => document.getElementById('import-file-input')?.click()}
              className="cap-label cursor-pointer hover:!text-ink"
            >
              Import
            </button>
            <button
              type="button"
              onClick={() => setResetDialog(true)}
              disabled={saving}
              className="cap-label cursor-pointer hover:!text-ink disabled:cursor-default disabled:opacity-60"
            >
              Reset to defaults
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || changedCount === 0}
              className="cursor-pointer bg-accent px-3 py-1 text-scale-1 font-semibold text-paper hover:bg-accent-hover disabled:cursor-default disabled:opacity-60"
            >
              Save changes ({changedCount})
            </button>
          </span>
        ) : undefined
      }
    >
      {!canEditSettings && (
        <p role="alert" className="mb-3 border border-sev-high/40 px-3 py-2 text-scale-1 text-sev-high">
          You do not have permission to modify settings. Settings are displayed in read-only mode.
        </p>
      )}

      <div className="flex flex-wrap items-baseline pt-2">
        {settingsByCategory.map((category, index) => {
          const on = index === safeIndex;
          return (
            <button
              key={category.category}
              type="button"
              onClick={() => setActiveTab(index)}
              aria-current={on ? 'true' : undefined}
              className={tabClass(on)}
            >
              {categoryLabels[category.category] || category.category_display}
            </button>
          );
        })}
      </div>

      <SettingsTab
        settings={currentCategory?.settings ?? []}
        values={values}
        onChange={handleChange}
        errors={errors}
        disabled={!canEditSettings || saving}
        loading={isPending}
      />

      {currentCategory?.category === SettingCategory.EMAIL && (
        <EmailTestPanel
          testModeOn={String(savedTestMode).toLowerCase() === 'true'}
          pendingChange={emailTestModePending}
          disabled={!isSuperuser || saving}
        />
      )}

      <input
        id="import-file-input"
        type="file"
        accept=".json"
        style={{ display: 'none' }}
        onChange={handleImport}
      />

      {/* Change Reason Dialog */}
      <LedgerDialog
        open={changeReasonDialog}
        onOpenChange={(next) => {
          if (!next) setChangeReasonDialog(false);
        }}
        title="Save Settings"
        description="Please provide a reason for these changes (optional):"
        size="sm"
        footer={
          <>
            <button
              type="button"
              onClick={() => setChangeReasonDialog(false)}
              className="cursor-pointer border border-ink px-3 py-1.5 text-scale-1 text-ink hover:bg-paper-raised"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmSave}
              className="cursor-pointer bg-accent px-3 py-1.5 text-scale-1 font-semibold text-paper hover:bg-accent-hover"
            >
              Save Changes
            </button>
          </>
        }
      >
        <LedgerField label="Change reason" htmlFor="settings-change-reason">
          <textarea
            id="settings-change-reason"
            rows={3}
            value={changeReason}
            onChange={(e) => setChangeReason(e.target.value)}
            placeholder="e.g., Updated email configuration for new SMTP server"
            className="w-full border border-rule-faint bg-transparent p-2 text-scale-1 text-ink outline-none focus:border-accent"
          />
        </LedgerField>
      </LedgerDialog>

      {/* Reset Confirmation Dialog */}
      <ConfirmDialog
        open={resetDialog}
        title="Reset Settings to Defaults?"
        message={`${
          Object.keys(changedValues).length > 0
            ? `This will reset ${Object.keys(changedValues).length} changed settings to their default values.`
            : 'This will reset all settings in the current category to their default values.'
        } This action cannot be undone.`}
        confirmText="Reset to Defaults"
        severity="error"
        onConfirm={handleReset}
        onCancel={() => setResetDialog(false)}
      />
    </PageWrapper>
  );
};

export default SettingsPage;
